<template>
  <a-modal
    :open="visible"
    :title="isOwnedQrEdit ? 'QR코드 상세' : (isEdit ? 'QR코드 수정' : 'QR코드 등록')"
    :width="720"
    :confirm-loading="loading"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="modal-content">
      <div v-if="isOwnedQrEdit" class="input-hint warning bundle-lock-notice">
        <i class="fa-solid fa-lock"></i>
        {{ ownedQrMeta(editData).notice }}
      </div>
      <a-form
        :disabled="isOwnedQrEdit"
        :model="formData"
        :label-col="{ span: 5 }"
        :wrapper-col="{ span: 19 }"
        layout="horizontal"
      >
        <!-- QR코드명 -->
        <a-form-item label="QR코드명" required>
          <a-input
            v-model:value="formData.name"
            placeholder="QR코드명을 입력하세요"
            :maxlength="100"
          />
        </a-form-item>

        <!-- 설명 -->
        <a-form-item label="설명">
          <a-textarea
            v-model:value="formData.description"
            placeholder="QR코드 설명을 입력하세요"
            :rows="3"
            :maxlength="500"
          />
        </a-form-item>

        <!-- 목적지 URL -->
        <a-form-item label="목적지 URL" required>
          <a-input
            v-model:value="formData.targetUrl"
            placeholder="https://example.com"
            :disabled="isDirectUrlEdit || isOwnedQrEdit"
            @blur="validateUrl"
          />
          <div class="input-hint" v-if="!hasKorean && !isDirectUrlEdit && !isOwnedQrEdit">QR코드 스캔 시 이동할 URL을 입력하세요</div>
          <div class="input-hint info" v-if="isValidUrl && hasKorean && !isDirectUrlEdit && !isOwnedQrEdit">
            <i class="fa-solid fa-circle-info"></i>
            한글이 포함된 URL은 자동으로 인코딩되어 등록됩니다.
          </div>
          <div class="input-hint warning" v-if="isDirectUrlEdit">
            <i class="fa-solid fa-lock"></i>
            직접 URL QR코드는 URL 변경이 불가능합니다.
          </div>
        </a-form-item>

        <!-- 카테고리 -->
        <a-form-item label="카테고리">
          <a-select
            v-model:value="formData.categoryId"
            placeholder="카테고리 선택"
            allowClear
            style="width: 100%"
          >
            <a-select-option v-for="cat in categories" :key="cat._id" :value="cat._id">
              {{ cat.name }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <!-- 태그 -->
        <a-form-item label="태그">
          <a-select
            v-model:value="formData.tagIds"
            mode="multiple"
            placeholder="태그 선택 (다중 선택 가능)"
            allowClear
            style="width: 100%"
          >
            <a-select-option v-for="tag in tags" :key="tag._id" :value="tag._id">
              <span
                class="tag-option"
                :style="{ backgroundColor: (tag.color || '#007bff') + '20', color: tag.color || '#007bff' }"
              >
                {{ tag.name }}
              </span>
            </a-select-option>
          </a-select>
        </a-form-item>

        <!-- 상태 -->
        <a-form-item label="상태">
          <a-radio-group v-model:value="formData.status">
            <a-radio value="active">활성</a-radio>
            <a-radio value="inactive">비활성</a-radio>
          </a-radio-group>
        </a-form-item>

        <!-- 통계 수집 (등록 시에만) -->
        <a-form-item label="통계 수집">
          <template v-if="!isEdit">
            <a-switch
              v-model:checked="formData.trackingEnabled"
              checked-children="사용"
              un-checked-children="미사용"
            />
            <div class="tracking-info-box" :class="{ 'tracking-disabled': !formData.trackingEnabled }">
              <div v-if="formData.trackingEnabled" class="tracking-info enabled">
                <i class="fa-solid fa-chart-line"></i>
                <div class="info-content">
                  <span class="info-title">통계 수집 QR코드</span>
                  <span class="info-desc">스캔 통계가 수집되며, 목적지 URL을 언제든 변경할 수 있습니다.</span>
                </div>
              </div>
              <div v-else class="tracking-info disabled">
                <i class="fa-solid fa-link"></i>
                <div class="info-content">
                  <span class="info-title">직접 URL QR코드</span>
                  <span class="info-desc">QR코드가 목적지 URL로 직접 연결됩니다. 스캔 통계가 수집되지 않으며, <strong>등록 후 URL 변경이 불가능</strong>합니다.</span>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <a-tag :color="editData?.trackingEnabled !== false ? 'blue' : 'default'">
              {{ editData?.trackingEnabled !== false ? '통계 수집 중' : '직접 URL (통계 미수집)' }}
            </a-tag>
            <div class="edit-tracking-hint">
              <i class="fa-solid fa-circle-info"></i>
              통계 수집 설정은 등록 시에만 선택할 수 있으며, 이후 변경할 수 없습니다.
            </div>
          </template>
        </a-form-item>
      </a-form>

      <div v-if="isOwnedQrEdit" class="modal-footer">
        <a-button @click="handleCancel">닫기</a-button>
      </div>
      <div v-else class="modal-footer">
        <a-button @click="handleCancel">취소</a-button>
        <a-button type="primary" class="yellow-button" :loading="loading" @click="handleSubmit">
          {{ isEdit ? '수정' : '등록' }}
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue';
import { message } from 'ant-design-vue';
import { isOwnedQr, ownedQrMeta } from '../utils/qrUrls';

export default defineComponent({
  name: 'QrFormModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    editData: {
      type: Object,
      default: null
    },
    categories: {
      type: Array,
      default: () => []
    },
    tags: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:visible', 'submit'],
  setup(props, { emit }) {
    const loading = ref(false);
    const isValidUrl = ref(false);
    const hasKorean = ref(false);

    const defaultFormData = {
      name: '',
      description: '',
      targetUrl: '',
      categoryId: undefined,
      tagIds: [],
      status: 'active',
      trackingEnabled: true
    };

    const formData = ref({ ...defaultFormData });

    const isEdit = computed(() => !!props.editData);
    // 직접 URL QR코드 수정 모드 (trackingEnabled가 false인 QR코드 수정 시)
    const isDirectUrlEdit = computed(() => isEdit.value && props.editData?.trackingEnabled === false);
    // 소유 QR(상담 파일 묶음·시나리오 배포 링크 등)은 읽기 전용 상세 모드 (변경은 소유 서비스에서만)
    const isOwnedQrEdit = computed(() => isEdit.value && isOwnedQr(props.editData));

    const validateUrl = () => {
      // 한글 포함 여부 체크
      const koreanPattern = /[가-힣]/;
      hasKorean.value = koreanPattern.test(formData.value.targetUrl);

      // URL 유효성 검사 (한글 및 유니코드 문자 허용)
      try {
        const url = formData.value.targetUrl?.trim();
        if (!url) {
          isValidUrl.value = false;
          return;
        }

        // http/https가 없으면 https 붙여서 검사
        const urlToTest = url.match(/^https?:\/\//) ? url : `https://${url}`;
        new URL(urlToTest);
        isValidUrl.value = true;
      } catch {
        isValidUrl.value = false;
      }
    };

    // tagIds에서 안전하게 ID 추출 (객체 배열 또는 문자열 배열 모두 지원)
    const extractTagIds = (tagIds) => {
      if (!tagIds || !Array.isArray(tagIds)) return [];
      return tagIds
        .filter(t => t) // null/undefined 제거
        .map(t => {
          // 객체인 경우 _id 추출, 문자열인 경우 그대로 사용
          if (typeof t === 'object' && t._id) return t._id;
          if (typeof t === 'string') return t;
          return null;
        })
        .filter(id => id); // null 제거
    };

    watch(() => props.visible, (newVal) => {
      if (newVal) {
        if (props.editData) {
          formData.value = {
            name: props.editData.name || '',
            description: props.editData.description || '',
            targetUrl: props.editData.targetUrl || '',
            categoryId: props.editData.categoryId?._id || props.editData.categoryId || undefined,
            tagIds: extractTagIds(props.editData.tagIds),
            status: props.editData.status || 'active',
            trackingEnabled: props.editData.trackingEnabled !== false
          };
          validateUrl();
        } else {
          formData.value = { ...defaultFormData };
          isValidUrl.value = false;
        }
      }
    });

    watch(() => formData.value.targetUrl, () => {
      validateUrl();
    });

    const handleCancel = () => {
      emit('update:visible', false);
    };

    const handleSubmit = async () => {
      // 유효성 검사
      if (!formData.value.name?.trim()) {
        message.warning('QR코드명을 입력해주세요.');
        return;
      }

      if (!formData.value.targetUrl?.trim()) {
        message.warning('목적지 URL을 입력해주세요.');
        return;
      }

      if (!isValidUrl.value) {
        message.warning('올바른 URL 형식을 입력해주세요.');
        return;
      }

      loading.value = true;

      try {
        // URL 인코딩은 QrService에서 처리됨
        const submitData = {
          name: formData.value.name.trim(),
          description: formData.value.description?.trim() || '',
          targetUrl: formData.value.targetUrl.trim(),
          categoryId: formData.value.categoryId || null,
          tagIds: formData.value.tagIds || [],
          status: formData.value.status
        };

        // trackingEnabled는 등록 시에만 설정 가능 (수정 시 제외)
        if (!isEdit.value) {
          submitData.trackingEnabled = formData.value.trackingEnabled;
        }

        emit('submit', submitData, isEdit.value);
      } finally {
        loading.value = false;
      }
    };

    return {
      loading,
      formData,
      isEdit,
      isDirectUrlEdit,
      isOwnedQrEdit,
      ownedQrMeta,
      isValidUrl,
      hasKorean,
      validateUrl,
      handleCancel,
      handleSubmit
    };
  }
});
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';

.modal-content {
  padding: $spacing-md 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: $spacing-sm;
  padding-top: $spacing-md;
  border-top: 1px solid $border-color;
  margin-top: $spacing-md;
}

.input-hint {
  font-size: $font-size-xs;
  color: $text-muted;
  margin-top: 4px;

  &.info {
    color: #1890ff;

    i {
      margin-right: 4px;
    }
  }

  &.warning {
    color: #fa8c16;

    i {
      margin-right: 4px;
    }
  }
}

.bundle-lock-notice {
  margin: 0 0 $spacing-md;
}

.tag-option {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: $font-size-xs;
}

.yellow-button {
  background-color: $yellow-button-color;
  border-color: $yellow-button-color;
  color: $text-dark;

  &:hover {
    background-color: $yellow-button-color-hover;
    border-color: $yellow-button-color-hover;
  }
}

.tracking-info-box {
  margin-top: $spacing-sm;
  padding: $spacing-md;
  border-radius: $border-radius-md;
  background-color: #e6f7ff;
  border: 1px solid #91d5ff;
  transition: all 0.3s ease;

  &.tracking-disabled {
    background-color: #fff7e6;
    border-color: #ffd591;
  }
}

.tracking-info {
  display: flex;
  align-items: flex-start;
  gap: $spacing-sm;

  i {
    font-size: 20px;
    margin-top: 2px;
  }

  &.enabled i {
    color: #1890ff;
  }

  &.disabled i {
    color: #fa8c16;
  }

  .info-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .info-title {
    font-weight: $font-weight-bold;
    color: $text-dark;
  }

  .info-desc {
    font-size: $font-size-sm;
    color: $text-muted;
    line-height: 1.5;

    strong {
      color: #fa541c;
    }
  }
}

.edit-tracking-hint {
  margin-top: $spacing-sm;
  font-size: $font-size-xs;
  color: $text-muted;
  display: flex;
  align-items: center;
  gap: 6px;

  i {
    color: #1890ff;
  }
}
</style>
